/**
 * PATH: packages/email-hub/src/feature/reader-header/lanes/useExpandedLanesState.ts
 * FILE_ID: EMAILHUB-READERHEADER-LANES-STATE-001
 * PURPOSE: Own the shared state + transports the four lanes and their menus need.
 * ROLE IN ARCHITECTURE: Behavior hook backing triage/ExpandedContext.
 * MAIN EXPORTS: useExpandedLanesState.
 * NON-RESPONSIBILITIES: No rendering; no routing precedence (menus/ owns it).
 * NOTES FOR FUTURE AI: askAi prefers the conversational co-pilot when a host
 *   is mounted (FEAT-2026-07-09-ASKAI-OPENS-COPILOT) exactly like the legacy
 *   strip. Copy paths reuse the toast contract from ReaderSignalStripMenus.
 */
import { useCallback, useMemo, useState } from 'react'

import { useToast } from '../../../_internal/components/Toast'
import {
  invoiceNumberSignalFor,
  rejectVerificationSignal,
  verificationCodeSignalFor,
} from '../../action-signals'
import { emailWithSenderIdentity } from '../../sender-identity'
import { hasAiCopilotHost, useAiCopilotStore } from '../../store/aiCopilotStore'
import type { Email } from '../../hooks/useEmails'
import type { LaneMenuDeps } from '../menus/laneMenuItems'

export interface ExpandedLanesInput {
  email: Email
  onFilterCompany?: () => void
  canRequestRule: boolean
  onOpenRuleDialog: () => void
}

/**
 * Shared lane state: overlays, dismissed signals, and menu transports.
 *
 * Inputs: email + host callbacks that vary by surface.
 * Output: overlay open state setters, resolved (dismissal-filtered)
 * signals, the identity-enriched email, and the LaneMenuDeps bundle the
 * item builder consumes.
 * Side effects: clipboard writes and toasts from menu copy items;
 * verification rejection POST from the reject transport.
 */
export function useExpandedLanesState({
  email,
  onFilterCompany,
  canRequestRule,
  onOpenRuleDialog,
}: ExpandedLanesInput) {
  const { toast } = useToast()
  const [companyContextOpen, setCompanyContextOpen] = useState(false)
  const [signalsManagerOpen, setSignalsManagerOpen] = useState(false)
  const [placementManagerOpen, setPlacementManagerOpen] = useState(false)
  const [dismissedSignals, setDismissedSignals] = useState<Set<string>>(
    () => new Set(),
  )

  const identityEmail = emailWithSenderIdentity(email)
  const verification = verificationCodeSignalFor(email)
  const invoice = invoiceNumberSignalFor(email)
  const activeVerification =
    verification && !dismissedSignals.has(verification.kind) ? verification : null
  const activeInvoice =
    invoice && !dismissedSignals.has(invoice.kind) ? invoice : null

  const dismissVerification = useCallback(() => {
    if (verification) {
      setDismissedSignals((current) => new Set(current).add(verification.kind))
    }
  }, [verification])

  const copyText = useCallback(
    async (value: string, label: string) => {
      try {
        await navigator.clipboard.writeText(value)
        toast(`Copied ${label}`, 'success')
      } catch {
        toast(`Couldn't copy ${label} — your browser blocked clipboard access.`, 'error')
      }
    },
    [toast],
  )

  const askAi = useCallback(
    (prompt: string) => {
      if (hasAiCopilotHost()) {
        useAiCopilotStore.getState().openFor(email.id, `${prompt} `)
        return
      }
      onOpenRuleDialog()
    },
    [email.id, onOpenRuleDialog],
  )

  const menuDeps = useMemo<LaneMenuDeps>(() => {
    const signalParts = [
      activeVerification ? `Code: ${activeVerification.display_value}` : null,
      activeInvoice ? `Invoice: ${activeInvoice.display_value}` : null,
      ...(email.tags?.map((tag) => `Tag: ${tag.name}`) ?? []),
      email.ai_summary?.trim() ? `AI summary: ${email.ai_summary}` : null,
    ].filter((part): part is string => part != null)
    return {
      askAi,
      onOpenCompanyContext: identityEmail.company_domain
        ? () => setCompanyContextOpen(true)
        : undefined,
      onShowCompanyEmails: onFilterCompany,
      canRequestRule,
      onRequestRule: onOpenRuleDialog,
      onOpenSignalsManager: () => setSignalsManagerOpen(true),
      onOpenPlacementManager: () => setPlacementManagerOpen(true),
      copyAiSummary: email.ai_summary?.trim()
        ? () => void copyText(email.ai_summary ?? '', 'AI summary')
        : undefined,
      copyAllSignals:
        signalParts.length > 0
          ? () => void copyText(signalParts.join('\n'), 'signals')
          : undefined,
      verification: activeVerification
        ? {
            onCopy: () =>
              void copyText(
                activeVerification.display_value,
                activeVerification.context.toLowerCase(),
              ),
            onReject: () =>
              void rejectVerificationSignal(email.id)
                .then(() => {
                  dismissVerification()
                  toast(
                    'Marked as not a verification code. The code itself was not saved.',
                    'success',
                  )
                })
                .catch(() =>
                  toast('Could not record that correction. Try again.', 'error'),
                ),
          }
        : null,
      invoice: activeInvoice
        ? {
            onCopy: () =>
              void copyText(activeInvoice.display_value, 'invoice number'),
          }
        : null,
    }
  }, [
    activeInvoice,
    activeVerification,
    askAi,
    canRequestRule,
    copyText,
    dismissVerification,
    email.ai_summary,
    email.id,
    email.tags,
    identityEmail.company_domain,
    onFilterCompany,
    onOpenRuleDialog,
    toast,
  ])

  return {
    identityEmail,
    activeVerification,
    activeInvoice,
    dismissVerification,
    companyContextOpen,
    setCompanyContextOpen,
    signalsManagerOpen,
    setSignalsManagerOpen,
    placementManagerOpen,
    setPlacementManagerOpen,
    menuDeps,
  }
}
